import { useMemo } from 'react';
import { Currency, Price } from '@uniswap/sdk-core';
import { tickToPrice } from '@uniswap/v4-sdk';
import { isToken0PreferredQuoteToken } from '@/utils/tokenPreference';
import type { PositionDetails } from '@/onchain/hooks/usePositions';

/**
 * Derived position data type
 * Contains computed values from position data that are expensive to calculate
 */
export type DerivedPositionData = {
  baseToken: PositionDetails['positionTokens'][0];
  quoteToken: PositionDetails['positionTokens'][0];
  tokenPair: string;
  tokenConversionLabel: string;
  priceLower: Price<Currency, Currency>;
  priceUpper: Price<Currency, Currency>;
};

/**
 * Hook to compute derived position data with memoization
 *
 * This hook calculates expensive derived values from position data:
 * - Determines base and quote tokens based on preference
 * - Formats token pair display string
 * - Converts ticks to prices using Uniswap SDK
 *
 * All values are memoized to prevent unnecessary recalculation on re-renders.
 *
 * @param position - The position details to derive data from
 * @returns Memoized derived position data
 */
export function useDerivedPositionData(position: PositionDetails): DerivedPositionData {
  return useMemo(() => {
    const { positionTokens, tickLower, tickUpper } = position;
    const token0AsQuoteToken = isToken0PreferredQuoteToken(
      positionTokens[0].symbol,
      positionTokens[1].symbol,
    );
    const baseToken = token0AsQuoteToken ? positionTokens[1] : positionTokens[0];
    const quoteToken = token0AsQuoteToken ? positionTokens[0] : positionTokens[1];
    const tokenPair = `${baseToken.symbol} / ${quoteToken.symbol}`;
    const tokenConversionLabel = `${quoteToken.symbol} per ${baseToken.symbol}`;
    const priceLowerRaw = tickToPrice(baseToken.token, quoteToken.token, tickLower);
    const priceUpperRaw = tickToPrice(baseToken.token, quoteToken.token, tickUpper);

    const pricesInverted = priceLowerRaw.greaterThan(priceUpperRaw);
    const priceLower = pricesInverted ? priceUpperRaw : priceLowerRaw;
    const priceUpper = pricesInverted ? priceLowerRaw : priceUpperRaw;

    return {
      baseToken,
      quoteToken,
      tokenPair,
      tokenConversionLabel,
      priceLower,
      priceUpper,
    };
  }, [position]);
}
